import React, { useState } from 'react'
import { Button, Card, Flex, H2, Input, Line, P, Span } from '../../lib/components'
import { InvestmentLimitRequestDetail, InvestmentLimitRequestDocument } from '../../lib/types'
import { displayDollar, getTimestampWithTimezone } from '../../lib/utils/helpers'
import './investment-limit-requests.scss'

export type InvestmentLimitRequestDetailPresenterProps = {
  request: InvestmentLimitRequestDetail | null
  isLoading: boolean
  isSubmitting: boolean
  canReview: boolean
  onBack: () => void
  onApprove: (limitDollars: number, note?: string) => Promise<void>
  onReject: (note?: string) => Promise<void>
}

const formatDate = (value?: string | null) => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return getTimestampWithTimezone(date, 'DD MMM YYYY, HH:mm z')
}

const DetailRow = ({ label, value, testId }: { label: string; value: string; testId?: string }) => (
  <Flex variant='space-between' classes={['mt-4']} dataTestId={testId}>
    <Span variant='bold' text={label} />
    <Span variant='thin' text={value} />
  </Flex>
)

const openDownload = (doc: InvestmentLimitRequestDocument) => {
  if (!doc.downloadUrl) return
  window.open(doc.downloadUrl, '_blank', 'noopener,noreferrer')
}

export function InvestmentLimitRequestDetailPresenter({
  request,
  isLoading,
  isSubmitting,
  canReview,
  onBack,
  onApprove,
  onReject,
}: InvestmentLimitRequestDetailPresenterProps) {
  const [approveLimit, setApproveLimit] = useState('')
  const [approveNote, setApproveNote] = useState('')
  const [rejectNote, setRejectNote] = useState('')

  const isSubmitted = request?.status === 'SUBMITTED'
  const showReviewActions = Boolean(request && isSubmitted && canReview)

  const submitApprove = async () => {
    const dollars = Number(approveLimit)
    await onApprove(dollars, approveNote)
  }

  const submitReject = async () => {
    await onReject(rejectNote)
  }

  return (
    <Flex variant='column-center'>
      <div className='investment-limit-requests investment-limit-requests--detail w-100'>
        <div className='investment-limit-requests__detail-header'>
          <H2 text='Investment Limit Request' />
          <Button
            text='Back to list'
            variant='secondary-btn'
            handleClick={onBack}
            classes={['investment-limit-requests__back-btn']}
            dataTestId='investment-limit-request-back'
          />
        </div>
        <Line />
        {isLoading ? (
          <p
            className='investment-limit-requests__loading'
            data-testid='investment-limit-request-detail-loading'
          >
            Loading…
          </p>
        ) : !request ? (
          <Span
            classes={['center', 'mt-24']}
            variant='thin'
            text='Investment limit request not found'
            dataTestId='investment-limit-request-detail-empty'
          />
        ) : (
          <>
            <Card classes={['p-40', 'mt-24']} dataTestId='investment-limit-request-detail-card'>
              <P text='Client & request' />
              <DetailRow
                label='Request ID'
                value={request.id}
                testId='investment-limit-request-detail-id'
              />
              <DetailRow
                label='Investor'
                value={request.investorName || '—'}
                testId='investment-limit-request-detail-name'
              />
              <DetailRow
                label='Email'
                value={request.investorEmail || '—'}
                testId='investment-limit-request-detail-email'
              />
              <DetailRow
                label='Client ID'
                value={request.clientId || '—'}
                testId='investment-limit-request-detail-client-id'
              />
              <DetailRow
                label='Investor ID'
                value={String(request.investorId)}
                testId='investment-limit-request-detail-investor-id'
              />
              {request.portfolioId ? (
                <DetailRow
                  label='Portfolio ID'
                  value={request.portfolioId}
                  testId='investment-limit-request-detail-portfolio-id'
                />
              ) : null}
              <DetailRow
                label='Status'
                value={request.status || '—'}
                testId='investment-limit-request-detail-status'
              />
              <DetailRow
                label='Requested range'
                value={request.requestedLimitRangeLabel || request.requestedLimitRange || '—'}
                testId='investment-limit-request-detail-range'
              />
              <DetailRow
                label='Current limit'
                value={displayDollar(String(request.currentInvestmentLimit ?? ''), 'USD', 2, true)}
                testId='investment-limit-request-detail-current-limit'
              />
              <DetailRow
                label='Submitted'
                value={formatDate(request.createdAt)}
                testId='investment-limit-request-detail-created'
              />
              <DetailRow
                label='Updated'
                value={formatDate(request.updatedAt)}
                testId='investment-limit-request-detail-updated'
              />
              {request.status !== 'SUBMITTED' ? (
                <>
                  <Line />
                  <P text='Review' />
                  <DetailRow
                    label='Reviewed at'
                    value={formatDate(request.reviewedAt)}
                    testId='investment-limit-request-detail-reviewed-at'
                  />
                  <DetailRow
                    label='Reviewed by'
                    value={request.reviewedByAdminEmail || '—'}
                    testId='investment-limit-request-detail-reviewed-by'
                  />
                  <DetailRow
                    label='Review note'
                    value={request.reviewNote || '—'}
                    testId='investment-limit-request-detail-review-note'
                  />
                </>
              ) : null}
            </Card>

            <Card
              classes={['p-40', 'mt-24']}
              dataTestId='investment-limit-request-detail-docs-card'
            >
              <P text='Documents' />
              {!request.documents?.length ? (
                <Span
                  classes={['mt-16']}
                  variant='thin'
                  text='No documents attached'
                  dataTestId='investment-limit-request-detail-docs-empty'
                />
              ) : (
                <ul className='investment-limit-requests__docs'>
                  {request.documents.map((doc) => (
                    <li
                      key={doc.fileId || `${doc.documentType}-${doc.fileName}`}
                      className='investment-limit-requests__doc-row'
                      data-testid={`investment-limit-request-doc-${doc.fileId}`}
                    >
                      <div className='investment-limit-requests__doc-meta'>
                        <Span
                          variant='bold'
                          text={doc.documentTypeLabel || doc.documentType || 'Document'}
                        />
                        <Span variant='thin' text={doc.fileName || '—'} />
                      </div>
                      <Button
                        text='Download'
                        variant='secondary-btn'
                        classes={['investment-limit-requests__doc-download']}
                        handleClick={() => openDownload(doc)}
                        isDisabled={!doc.downloadUrl}
                        dataTestId={`investment-limit-request-doc-download-${doc.fileId}`}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            {showReviewActions ? (
              <Card
                classes={['p-40', 'mt-24']}
                dataTestId='investment-limit-request-review-actions'
              >
                <P text='Review actions' />
                <Flex variant='column' classes={['mt-16', 'gap-12']}>
                  <Input
                    name='approveLimit'
                    placeholder='New investment limit (USD)'
                    type='number'
                    value={approveLimit}
                    handleChange={(e) => setApproveLimit(e.target.value)}
                    dataTestId='investment-limit-request-approve-limit-input'
                  />
                  <Input
                    name='approveNote'
                    placeholder='Optional approve note'
                    value={approveNote}
                    handleChange={(e) => setApproveNote(e.target.value)}
                    dataTestId='investment-limit-request-approve-note-input'
                  />
                  <Button
                    text={isSubmitting ? 'Approving…' : 'Approve'}
                    variant='primary-btn'
                    classes={['auto-btn-width']}
                    handleClick={() => {
                      void submitApprove()
                    }}
                    isDisabled={isSubmitting || !approveLimit}
                    dataTestId='investment-limit-request-approve-button'
                  />
                </Flex>
                <Line />
                <Flex variant='column' classes={['mt-16', 'gap-12']}>
                  <Input
                    name='rejectNote'
                    placeholder='Optional reject reason'
                    value={rejectNote}
                    handleChange={(e) => setRejectNote(e.target.value)}
                    dataTestId='investment-limit-request-reject-note-input'
                  />
                  <Button
                    text={isSubmitting ? 'Rejecting…' : 'Reject'}
                    variant='secondary-btn'
                    classes={['auto-btn-width']}
                    handleClick={() => {
                      void submitReject()
                    }}
                    isDisabled={isSubmitting}
                    dataTestId='investment-limit-request-reject-button'
                  />
                </Flex>
              </Card>
            ) : null}
          </>
        )}
      </div>
    </Flex>
  )
}
